import React from 'react';
import { User, Shield, BarChart3 } from 'lucide-react';
import { ThemeContext } from '../constant/theme';
import { LanguageContext } from '../constant/LanguageContext';

// Purely presentational and fully controlled: `viewType` is the ONLY source
// of truth for which segment is active, and it always comes from the parent
// shell's own state (the same state that decides what content is rendered).
// This component holds no state, reads no localStorage, and listens to no
// events — there is nothing here that can drift from what the page is actually
// showing. Persistence (localStorage) and navigation are the parent's
// responsibility, done inside `onSelect`.
//
// ── The interaction ──────────────────────────────────────────────────────
// Only the SELECTED view shows its name; the others are icon-only. Selecting a
// different one expands its label while the previous one collapses, so the
// control grows and shrinks instead of snapping.
//
// Both halves of that are pure CSS, which is what keeps it smooth and keeps
// this component free of layout measurement:
//
//   * the label animates its `max-width` from 0, so the segment's width is
//     interpolated by the browser rather than jumping between two sizes;
//   * the highlight is a `::before` gradient that cross-fades on `opacity`,
//     so it appears to grow into place along with the label.
//
// The earlier version toggled the label with no transition at all, which is
// why switching read as a lurch — the width changed in a single frame. It also
// marked the active button `disabled`, so the browser painted the selected
// view with its greyed-out treatment; that is why the highlight looked faded.
const ViewTypeSwitcher = ({ viewType, onSelect, hasAdminLevel, role }) => {
  const { theme } = React.useContext(ThemeContext);
  const { language } = React.useContext(LanguageContext);
  const isAr = language === 'ar';
  const isLight = theme === 'light';

  const labels = {
    user: isAr ? 'مستخدم' : 'User',
    manager: isAr ? 'مدير' : 'Manager',
    admin: isAr ? 'مسؤول' : 'Admin',
  };

  // Inactive segments are icon-only, so the tooltip is the only thing naming
  // them on hover. The label itself stays in the DOM (clipped, not removed) so
  // screen readers still announce every option.
  const titles = {
    user: isAr ? 'عرض المستخدم' : 'User View',
    manager: isAr ? 'عرض المدير' : 'Manager View',
    admin: isAr ? 'عرض المسؤول' : 'Admin View',
  };

  const view = viewType || 'user';

  const showAdmin = (() => {
    try {
      if (role === 'admin') return true;
      if (typeof hasAdminLevel === 'function') return hasAdminLevel();
      return false;
    } catch (e) { return false; }
  })();

  const showManager = (() => {
    try {
      return role === 'manager';
    } catch (e) { return false; }
  })();

  const doSelect = (type) => {
    if (type === view) return;
    // Only reachable views are rendered below, but guard anyway in case a
    // stale role/hasAdminLevel prop ever left a disallowed segment clickable.
    if (type === 'manager' && !showManager) return;
    if (type === 'admin' && !showAdmin) return;
    if (typeof onSelect === 'function') onSelect(type);
  };

  const options = [
    { type: 'user', Icon: User, visible: true },
    { type: 'manager', Icon: BarChart3, visible: showManager },
    { type: 'admin', Icon: Shield, visible: showAdmin },
  ].filter((option) => option.visible);

  // Arabic reads right-to-left, so the segments are reversed rather than the
  // container being flipped — the DOM order is then exactly the visual order,
  // which keeps hit-testing and focus order honest in both languages.
  const displayed = isAr ? [...options].reverse() : options;

  const trackBg = isLight ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.07)';
  const idleColor = isLight ? '#64748b' : 'rgba(255,255,255,0.62)';
  const hoverColor = isLight ? '#0f172a' : '#ffffff';
  const hoverBg = isLight ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.10)';

  return (
    <div
      className="vts-track"
      role="group"
      aria-label={isAr ? 'تبديل العرض' : 'Switch view'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: 3,
        // Fully rounded capsule — the radius exceeds half the height, so the
        // ends stay semicircular at every width the label animation passes
        // through.
        borderRadius: 9999,
        background: trackBg,
        border: isLight ? '1px solid rgba(15,23,42,0.06)' : '1px solid rgba(255,255,255,0.08)',
        userSelect: 'none',
      }}
    >
      <style>{`
        .vts-seg {
          position: relative;
          isolation: isolate;
          -webkit-tap-highlight-color: transparent;
        }
        /* The highlight. A pseudo-element so the gradient can cross-fade --
           gradients are not interpolable, but opacity and transform are. It
           also scales up as it appears, so the selection lands with some
           weight instead of merely fading in. */
        .vts-seg::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: -1;
          border-radius: 9999px;
          background: linear-gradient(135deg, #3a8fd0 0%, #1e5a96 100%);
          box-shadow: 0 2px 10px rgba(30,90,150,0.36);
          opacity: 0;
          transform: scale(0.86);
          transition: opacity 190ms ease, transform 190ms ease;
        }
        .vts-seg.is-active::before {
          opacity: 1;
          transform: scale(1);
          transition:
            opacity 260ms ease,
            transform 440ms cubic-bezier(0.34, 1.5, 0.64, 1);
        }

        /* Hover tint for the unselected segments, on its own layer so it never
           fights the highlight above. */
        .vts-seg::after {
          content: '';
          position: absolute;
          inset: 0;
          z-index: -2;
          border-radius: 9999px;
          background: ${hoverBg};
          opacity: 0;
          transition: opacity 180ms ease;
        }
        .vts-seg:not(.is-active):hover::after { opacity: 1; }
        .vts-seg:not(.is-active):hover { color: ${hoverColor}; }
        .vts-seg:not(.is-active):hover .vts-ico { transform: scale(1.1); }
        .vts-seg:active .vts-ico { transform: scale(0.92); }

        .vts-seg:focus-visible {
          outline: 2px solid #2e7cba;
          outline-offset: 2px;
          border-radius: 9999px;
        }

        /* A one-shot pop each time a segment becomes the selected one. It
           re-runs on every selection because the class is re-applied, which is
           what makes the change register at a glance. */
        @keyframes vts-pop {
          0%   { transform: scale(0.78); }
          58%  { transform: scale(1.16); }
          100% { transform: scale(1); }
        }
        .vts-seg.is-active .vts-ico {
          animation: vts-pop 440ms cubic-bezier(0.34, 1.4, 0.64, 1);
        }

        /* The label expands from zero width instead of popping into place.
           max-width (not width) is what makes this animatable without knowing
           the text's size up front; the value only needs to exceed the longest
           label. It also slides out from behind the icon, and its fade is
           delayed a beat so the width visibly opens BEFORE the text arrives --
           the stagger is what makes the motion read as deliberate rather than
           as a flicker. */
        .vts-lbl {
          display: inline-block;
          max-width: 0;
          opacity: 0;
          overflow: hidden;
          white-space: nowrap;
          transform: translateX(-6px);
          /* Collapsing again is quick and unfussy -- only the reveal is showy. */
          transition:
            max-width 220ms ease,
            margin 220ms ease,
            opacity 130ms ease,
            transform 220ms ease;
        }
        .vts-seg.is-active .vts-lbl {
          max-width: 130px;
          opacity: 1;
          transform: translateX(0);
          margin-inline-start: 7px;
          transition:
            max-width 440ms cubic-bezier(0.34, 1.4, 0.64, 1),
            margin 440ms cubic-bezier(0.34, 1.4, 0.64, 1),
            opacity 260ms ease 90ms,
            transform 420ms cubic-bezier(0.34, 1.4, 0.64, 1) 70ms;
        }

        @media (prefers-reduced-motion: reduce) {
          .vts-seg::before, .vts-seg::after, .vts-lbl, .vts-ico {
            transition: none !important;
            animation: none !important;
          }
          .vts-lbl { transform: none; }
        }
      `}</style>

      {displayed.map(({ type, Icon }) => {
        const isActive = view === type;
        return (
          <button
            key={type}
            type="button"
            className={`vts-seg${isActive ? ' is-active' : ''}`}
            onClick={() => doSelect(type)}
            // Deliberately NOT `disabled` when active: a disabled button gets
            // the browser's washed-out treatment and loses its tooltip and
            // hover. doSelect() already no-ops for the current view.
            aria-pressed={isActive}
            title={titles[type]}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '7px 11px',
              border: 'none',
              background: 'transparent',
              borderRadius: 9999,
              cursor: isActive ? 'default' : 'pointer',
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              color: isActive ? '#ffffff' : idleColor,
              transition: 'color 220ms ease',
            }}
          >
            <Icon
              size={15}
              className="vts-ico"
              style={{
                flexShrink: 0,
                transition: 'transform 200ms cubic-bezier(0.34, 1.32, 0.58, 1)',
              }}
            />
            <span className="vts-lbl">{labels[type]}</span>
          </button>
        );
      })}
    </div>
  );
};

export default ViewTypeSwitcher;
